import { Grid, Skeleton } from '@mui/material';
import { Field, Formik, FormikErrors } from 'formik';
import { useSnackbar } from 'notistack';
import { useMutation, useQueryClient } from 'react-query';
import {
	CerebroDrawer,
	CerebroDropdown,
	CerebroDropdownMenuItem,
	CerebroFileDragAndDrop,
	CerebroForm,
	CerebroHeading,
	CerebroHierarchyFolderTree,
	CerebroInput,
	CerebroLabel,
	CerebroPrimaryButton,
	CerebroRichText,
	CerebroStyledBox,
	CerebroText,
	CerebroTextarea,
	CerebroYesNo,
} from '../../shared/components';
import { useApi, useAuth } from '../../shared/context';
import { QueryKeys, useActiveDataRoomUsersQuery, useDataRoomFolderHierarchyQuery } from '../../shared/hooks';
import { DataRoomDiligenceRequest, DiligenceRequestType, ShowDiligenceRequestFiles } from '../../shared/models';
import { FormikUtils } from '../../shared/utils';
import { CerebroApiEndpoints } from '../../shared/utils/api';
import { getApiResponseData } from '../../shared/utils/axios';
import { RequestFileDisplay } from './RequestFileDisplay';

interface EditQuestionFormProps {
	dataRoomGuid: string | undefined;
	request: DataRoomDiligenceRequest | null;
	close: () => void;
}

export const EditQuestionForm: React.FC<EditQuestionFormProps> = ({ dataRoomGuid, request, close }) => {
	const api = useApi();
	const snackbar = useSnackbar();
	const client = useQueryClient();
	var { currentUser } = useAuth();
	const isVisibleToAll = request?.isVisibleToAll ?? true;

	const lenderQuery = useActiveDataRoomUsersQuery(dataRoomGuid);
	const folderQuery = useDataRoomFolderHierarchyQuery(dataRoomGuid);

	const editRequestMutation = useMutation(
		async ({ values }: EditQuestionFormMutationVariables) => {
			let formData = new FormData();
			if (values.lenderGuid) {
				formData.append('lenderGuid', values.lenderGuid);
			}
			const isCompleted = values.resolution && values.resolution.length > 0;

			formData.append('title', values.title ?? '');
			formData.append('diligenceType', values.diligenceType ?? 'DocumentRequest');
			formData.append('details', values.details ?? '');
			formData.append('resolution', values.resolution ?? '');
			formData.append('isVisibleToAll', values.isVisibleToAll ? 'true' : 'false');
			formData.append('isCompleted', isCompleted ? 'true' : 'false');
			formData.append('folderGuid', values.folderGuid ?? '');
			formData.append('sendEmail', values.sendEmail ? 'true' : 'false');
			formData.append('isWithdrawn', values.isWithdrawn ? 'true' : 'false');

			const uploads = values.uploads;
			if (uploads && uploads.length > 0) {
				uploads.forEach(upload => {
					formData.append('uploads', upload, upload.name);
				});
			}

			const { data, error } = await getApiResponseData<DataRoomDiligenceRequest>(
				api.patch(
					CerebroApiEndpoints.DataRoomDiligenceRequests.Specific(dataRoomGuid, request?.guid),
					formData,
					{
						headers: { 'Content-Type': 'multipart/form-data' },
					}
				)
			);

			if (error) {
				throw new Error(error.response?.data);
			}

			return data;
		},
		{
			onSuccess: () => {
				if (currentUser.isBankUser) {
					client.invalidateQueries(QueryKeys.DataRoomDiligenceRequests.Lender(dataRoomGuid));
				} else {
					client.invalidateQueries(QueryKeys.DataRoomDiligenceRequests.Root(dataRoomGuid));
				}
				snackbar.enqueueSnackbar('Diligence request was updated.', { variant: 'success' });
				close();
			},
			onError: (error: Error) => {
				snackbar.enqueueSnackbar(error.message, { variant: 'error' });
			},
			onSettled: (_data, _error, { helpers }) => {
				helpers.setSubmitting(false);
			},
		}
	);

	return (
		<CerebroDrawer open={request != null} onClose={close}>
			<CerebroHeading size="h1" component="h1">
				Edit Diligence Request
			</CerebroHeading>

			<Formik
				initialValues={{
					lenderGuid: request?.dataRoomUserGuid,
					title: request?.title,
					diligenceType: request?.diligenceType,
					details: request?.details ?? '',
					resolution: request?.resolution,
					folderGuid: request?.folder.guid,
					sendEmail: true,
					isVisibleToAll: isVisibleToAll,
					isWithdrawn: request?.isWithdrawn ?? false,
					uploads: [] as File[],
					addFiles: false,
				}}
				validate={validateForm}
				onSubmit={FormikUtils.MutateOnSubmit(editRequestMutation.mutate)}
			>
				{({ isSubmitting, errors, touched, handleSubmit, values, setFieldValue }) => {
					return (
						<CerebroForm onSubmit={handleSubmit}>
							<Grid
								container
								spacing={3}
								sx={{ marginTop: 3 }}
								justifyContent={{ xs: 'center', md: 'flex-start' }}
							>
								{currentUser.isEmployeeUser && (
									<Grid item xs={12}>
										<CerebroLabel id="lenderGuid">Lender</CerebroLabel>
										{lenderQuery.isLoading && <Skeleton height={60} />}
										{lenderQuery.isSuccess && (
											<Field
												id="lenderGuid"
												as={CerebroDropdown}
												name="lenderGuid"
												disabled={isSubmitting}
											>
												{lenderQuery.data.map(lender => (
													<CerebroDropdownMenuItem key={lender.guid} value={lender.guid}>
														{lender.fullName} ({lender.bankName})
													</CerebroDropdownMenuItem>
												))}
											</Field>
										)}
									</Grid>
								)}

								{currentUser.isCompanyUser ? (
									<Grid item xs={12}>
										<CerebroLabel required id="title">
											{values.diligenceType == 'Question' ? 'Question' : 'Requested Documents'}
										</CerebroLabel>
										<CerebroText>{values.title}</CerebroText>
									</Grid>
								) : (
									<Grid item xs={12}>
										<CerebroLabel required id="title">
											{values.diligenceType == 'Question' ? 'Question' : 'Requested Documents'}
										</CerebroLabel>
										<Field
											id="title"
											as={CerebroInput}
											name="title"
											disabled={isSubmitting}
											error={FormikUtils.IsInvalid('title', errors, touched)}
										/>
										{FormikUtils.IsInvalid('title', errors, touched) && (
											<CerebroText variant="error">{errors.title}</CerebroText>
										)}
									</Grid>
								)}

								{currentUser.isCompanyUser ? (
									<Grid item xs={12}>
										<CerebroLabel id="details">Additional Details / Context</CerebroLabel>
										<CerebroText>{values.details}</CerebroText>
									</Grid>
								) : (
									<Grid item xs={12}>
										<CerebroLabel id="details">Additional Details / Context</CerebroLabel>
										<Field
											id="details"
											as={CerebroTextarea}
											name="details"
											rows={5}
											disabled={isSubmitting}
											error={FormikUtils.IsInvalid('details', errors, touched)}
										/>
										{FormikUtils.IsInvalid('details', errors, touched) && (
											<CerebroText variant="error">{errors.details}</CerebroText>
										)}
									</Grid>
								)}

								{ShowDiligenceRequestFiles(request) && (
									<RequestFileDisplay
										dataRoomGuid={dataRoomGuid}
										request={request}
										currentUser={currentUser}
									/>
								)}

								{!currentUser.isBankUser && (
									<>
										<Grid item xs={12}>
											<CerebroLabel id="resolution">Response</CerebroLabel>
											<CerebroRichText
												value={values.resolution ?? ''}
												onChange={updatedValue => setFieldValue('resolution', updatedValue)}
											/>
											{FormikUtils.IsInvalid('resolution', errors, touched) && (
												<CerebroText variant="error">Required</CerebroText>
											)}
										</Grid>
										<Grid item xs={12}>
											<CerebroLabel id="addFiles">Want to upload files?</CerebroLabel>
											<Field
												id="addFiles"
												as={CerebroYesNo}
												name="addFiles"
												disabled={isSubmitting}
											/>
										</Grid>
										{values.addFiles && (
											<>
												<Grid item xs={11}>
													<CerebroLabel id="uploads">Select File to Upload</CerebroLabel>
													<Field
														as={CerebroFileDragAndDrop}
														name="uploads"
														onlyPdf={false}
														multiple={true}
														sx={{ marginY: 3, width: '100%' }}
													/>
												</Grid>
												<Grid item xs={12}>
													<CerebroLabel required id="folderGuid">
														Data Room Folder
													</CerebroLabel>
													{folderQuery.isLoading && <Skeleton height={60} />}
													{folderQuery.isSuccess && (
														<CerebroStyledBox alignitems="left" sx={{ marginTop: 1 }}>
															<CerebroHierarchyFolderTree
																folder={folderQuery.data}
																selectedGuid={values.folderGuid}
																hidePrivateFolders={false}
																onSelected={value => {
																	values.folderGuid = value;
																	setFieldValue('folderGuid', value);
																}}
															/>
														</CerebroStyledBox>
													)}
												</Grid>
											</>
										)}
									</>
								)}

								{!currentUser.isBankUser && (
									<>
										{!isVisibleToAll && (
											<Grid item xs={12}>
												<CerebroLabel id="isVisibleToAll">
													Is Visible to All Lenders
												</CerebroLabel>
												<Field
													id="isVisibleToAll"
													as={CerebroYesNo}
													name="isVisibleToAll"
													disabled={isSubmitting}
												/>
												<CerebroText variant="hint">
													Set to Yes, if you would like all active lenders to view this
													request and the response, and to be notified when it&apos;s
													complete.
												</CerebroText>
											</Grid>
										)}
									</>
								)}

								{currentUser.isEmployeeUser && (
									<Grid item xs={12}>
										<CerebroLabel id="sendEmail">Send Email to Borrower Contacts</CerebroLabel>
										<Field
											id="sendEmail"
											as={CerebroYesNo}
											name="sendEmail"
											disabled={isSubmitting}
										/>
										<CerebroText variant="hint">
											If checked, an email alert will be sent to all borrower contacts.
										</CerebroText>
									</Grid>
								)}

								{currentUser.isEmployeeUser && (
									<Grid item xs={12}>
										<CerebroLabel id="isWithdrawn">Withdrawn</CerebroLabel>
										<Field
											id="isWithdrawn"
											as={CerebroYesNo}
											name="isWithdrawn"
											disabled={isSubmitting}
										/>
										<CerebroText variant="hint">
											If checked, an the diligence request with be withdrawn and no longer active.
											This action cannot be undone.
										</CerebroText>
									</Grid>
								)}

								<Grid item xs={12}>
									<CerebroText variant="hint">
										The lender(s) associated with this request will receive an email notification to
										review your response.
									</CerebroText>
								</Grid>

								<Grid item xs={8}>
									<CerebroPrimaryButton loading={isSubmitting} type="submit">
										Save
									</CerebroPrimaryButton>
								</Grid>
							</Grid>
						</CerebroForm>
					);
				}}
			</Formik>
		</CerebroDrawer>
	);
};

type EditQuestionFormMutationVariables = FormikUtils.MutateOnSubmitVariables<EditQuestionFormValues>;
interface EditQuestionFormValues {
	lenderGuid: string | null | undefined;
	title: string | undefined;
	diligenceType: DiligenceRequestType | undefined;
	details: string;
	resolution: string | undefined;
	folderGuid: string | undefined;
	sendEmail: boolean;
	isVisibleToAll: boolean;
	uploads: File[];
	addFiles: boolean;
	isWithdrawn: boolean;
}
type EditQuestionFormValueErrors = FormikErrors<EditQuestionFormValues>;

function validateForm(values: EditQuestionFormValues) {
	const errors: EditQuestionFormValueErrors = {};

	if (!values.title) {
		const title = values.diligenceType == 'Question' ? 'Question' : 'Requested Documents';
		errors.title = `${title} is required`;
	} else {
		if (values.title.length > 255) {
			errors.title = 'Cannot exceed 255 characters';
		}
	}

	if (!values.folderGuid) {
		errors.folderGuid = 'Data room folder is required';
	}

	return errors;
}
